import { createSignal, onCleanup } from "solid-js"
import type { ServerConnection } from "@/context/server"
import { createJSONStorage } from "@/storage/browser"
import { deleteStarterMessage, listStarterMessagesEndpoint, saveStarterMessage, updateStarterMessageModel } from "@/transport/starter-messages"

export type StarterMessage = {
  sessionID: string
  messageID: string
  workspaceID: string
  sessionTitle: string
  role: "user" | "assistant"
  preview: string
  timeCreated: number
  /** Server-owned model selection applied to forks; captured on first save. */
  providerID?: string
  modelID?: string
  variant?: string
}

export type StarterModelSelection = { providerID: string; modelID: string; variant: string }

/**
 * Resolves a picker choice into a starter selection. An explicit level (from a
 * recent entry) wins; re-picking the current model keeps its level; a
 * different model starts at Auto because levels are model-specific.
 */
export function starterModelSelection(
  item: Pick<StarterMessage, "providerID" | "modelID" | "variant">,
  model: { providerID: string; modelID: string },
  options?: { variant?: string | null },
): StarterModelSelection {
  if (options && "variant" in options) return { ...model, variant: options.variant ?? "" }
  const same = model.providerID === item.providerID && model.modelID === item.modelID
  return { ...model, variant: same ? (item.variant ?? "") : "" }
}

const SERVER_CACHE_PREFIX = "mux.session.starters.cache.v1"
const STARTER_MESSAGE_MAX_ENTRIES = 500
const STARTER_MESSAGE_MAX_BYTES = 1024 * 1024
const listeners = new Set<() => void>()
let version = 0
let items: StarterMessage[] = []
let server: ServerConnection.HttpBase | undefined
let serverKey = ""
let generation = 0
let retryTimer: ReturnType<typeof setTimeout> | undefined
let retryAttempt = 0
const requests = new Set<AbortController>()

function connectionKey(target: ServerConnection.HttpBase) {
  return target.url
}

function clearRetry() {
  if (retryTimer !== undefined) clearTimeout(retryTimer)
  retryTimer = undefined
}

function scheduleRefreshRetry(target: ServerConnection.HttpBase, activeGeneration: number) {
  if (server !== target || generation !== activeGeneration || retryTimer !== undefined) return
  const delay = Math.min(30_000, 1_000 * 2 ** retryAttempt)
  retryAttempt += 1
  retryTimer = setTimeout(() => {
    retryTimer = undefined
    if (server !== target || generation !== activeGeneration) return
    void refreshStarterMessages().catch(() => scheduleRefreshRetry(target, activeGeneration))
  }, delay)
}

function notify() {
  version += 1
  for (const listener of listeners) listener()
}

function boundItems(value: StarterMessage[]) {
  const next: StarterMessage[] = []
  let bytes = 0
  for (const item of value.slice().sort((a, b) => b.timeCreated - a.timeCreated)) {
    const size = JSON.stringify(item).length
    if (next.length >= STARTER_MESSAGE_MAX_ENTRIES || bytes + size > STARTER_MESSAGE_MAX_BYTES) break
    next.push(item)
    bytes += size
  }
  return next
}

function validItems(value: unknown): StarterMessage[] {
  if (!Array.isArray(value)) return []
  return boundItems(value.filter(
    (item): item is StarterMessage =>
      !!item &&
      typeof item.sessionID === "string" &&
      !item.sessionID.startsWith("oracle-") &&
      typeof item.messageID === "string" &&
      typeof item.workspaceID === "string" &&
      typeof item.sessionTitle === "string" &&
      (item.role === "user" || item.role === "assistant") &&
      typeof item.preview === "string" &&
      typeof item.timeCreated === "number",
  ))
}

function serverCache(target: ServerConnection.HttpBase) {
  const key = `${SERVER_CACHE_PREFIX}:${target.url.replace(/\/+$/, "")}`
  return createJSONStorage<StarterMessage[]>(key, { decode: validItems })
}

function readServerCache(target: ServerConnection.HttpBase) {
  return serverCache(target).read([])
}

function writeServerCache(target: ServerConnection.HttpBase, next: StarterMessage[]) {
  serverCache(target).write(next, { removeWhen: (value) => value.length === 0 })
}

function sameItem(item: StarterMessage, next: StarterMessage) {
  return item.sessionID === next.sessionID &&
    item.messageID === next.messageID &&
    item.workspaceID === next.workspaceID &&
    item.sessionTitle === next.sessionTitle &&
    item.role === next.role &&
    item.preview === next.preview &&
    item.timeCreated === next.timeCreated &&
    (item.providerID ?? "") === (next.providerID ?? "") &&
    (item.modelID ?? "") === (next.modelID ?? "") &&
    (item.variant ?? "") === (next.variant ?? "")
}

function sameItems(left: readonly StarterMessage[], right: readonly StarterMessage[]) {
  if (left.length !== right.length) return false
  return left.every((item, index) => {
    const next = right[index]
    return !!next && sameItem(item, next)
  })
}

const starterKey = (item: StarterMessage) => `${item.sessionID}\u0000${item.messageID}`

/**
 * Keeps the current object for every starter whose content did not change.
 * Lists render rows by object, so a fresh object for an unchanged starter
 * would remount its row and close anything open in it, such as its pickers.
 */
function shareUnchanged(next: StarterMessage[]) {
  const current = new Map(items.map((item) => [starterKey(item), item]))
  return next.map((item) => {
    const kept = current.get(starterKey(item))
    return kept && sameItem(kept, item) ? kept : item
  })
}

function request<T>(run: (target: ServerConnection.HttpBase, signal: AbortSignal) => Promise<T>, target = server) {
  if (!target) return Promise.reject(new Error("Starter message server is unavailable"))
  const controller = new AbortController()
  requests.add(controller)
  return run(target, controller.signal)
    .finally(() => requests.delete(controller))
}

function replaceItem(target: ServerConnection.HttpBase, sessionID: string, messageID: string, update: (item: StarterMessage) => StarterMessage) {
  if (server !== target) return
  items = items.map((item) => (item.sessionID === sessionID && item.messageID === messageID ? update(item) : item))
  writeServerCache(target, items)
  notify()
}

async function persistStarter(item: StarterMessage, target = server) {
  const stored = await request((server, signal) => saveStarterMessage(server, item, signal), target)
  // The server captures the model on first save. Adopt it only while the
  // starter has no selection, so a stale save response cannot overwrite a
  // model the user chose while the save was in flight.
  if (target && stored.modelID) {
    replaceItem(target, item.sessionID, item.messageID, (current) => (current.modelID ? current : {
      ...current,
      providerID: stored.providerID,
      modelID: stored.modelID,
      variant: stored.variant,
    }))
  }
}

function sameModel(left: StarterMessage, right: StarterMessage) {
  return (left.providerID ?? "") === (right.providerID ?? "") &&
    (left.modelID ?? "") === (right.modelID ?? "") &&
    (left.variant ?? "") === (right.variant ?? "")
}

/**
 * Optimistically sets the model future forks of a starter use. Rejects after
 * rolling back when the machine refuses the change, so callers can report it.
 */
export async function setStarterMessageModel(sessionID: string, messageID: string, selection: StarterModelSelection) {
  const target = server
  if (!target) throw new Error("Connect to a machine to change a starter's model")
  if (!selection.providerID || !selection.modelID) throw new Error("Choose a model for this starter")
  const existing = items.find((item) => item.sessionID === sessionID && item.messageID === messageID)
  if (!existing) return
  const next = { ...existing, ...selection }
  if (sameModel(existing, next)) return
  replaceItem(target, sessionID, messageID, () => next)
  try {
    await request((server, signal) => updateStarterMessageModel(server, { sessionID, messageID, ...selection }, signal), target)
  } catch (error) {
    // A machine switch aborts in-flight requests; that is not a user-facing failure.
    if (server !== target) return
    // Leave newer selections alone; only undo this change.
    replaceItem(target, sessionID, messageID, (current) => (sameModel(current, next) ? {
      ...current,
      providerID: existing.providerID,
      modelID: existing.modelID,
      variant: existing.variant,
    } : current))
    throw error
  }
}

async function deleteStarter(sessionID: string, messageID: string, target = server) {
  await request((server, signal) => deleteStarterMessage(server, sessionID, messageID, signal), target)
}

export async function refreshStarterMessages() {
  const target = server
  const activeGeneration = generation
  if (!target) return
  const next = validItems(await request((server, signal) => listStarterMessagesEndpoint(server, signal), target))
  if (server !== target || generation !== activeGeneration) return
  writeServerCache(target, next)
  retryAttempt = 0
  clearRetry()
  if (sameItems(items, next)) return
  items = shareUnchanged(next)
  notify()
}

export async function configureStarterMessages(target: ServerConnection.HttpBase) {
  const nextKey = connectionKey(target)
  const changedMachine = serverKey !== nextKey
  server = target
  serverKey = nextKey
  const activeGeneration = ++generation
  clearRetry()
  for (const controller of requests) controller.abort()
  requests.clear()
  retryAttempt = 0
  if (changedMachine) {
    const cached = readServerCache(target)
    items = shareUnchanged(cached)
    // Listeners compare the machine too, so a switch notifies even when the lists match.
    notify()
  }

  if (server !== target || generation !== activeGeneration) return
  try {
    await refreshStarterMessages()
  } catch (error) {
    scheduleRefreshRetry(target, activeGeneration)
    throw error
  }
}

export function clearStarterMessageServer(target?: ServerConnection.HttpBase) {
  if (target && server !== target) return
  server = undefined
  serverKey = ""
  generation += 1
  retryAttempt = 0
  clearRetry()
  for (const controller of requests) controller.abort()
  requests.clear()
  items = []
  notify()
}

/** The machine whose starters `listStarterMessages` returns, by URL. */
export function starterMessagesServerURL() {
  return server?.url
}

export function starterMessagePreview(text: string, max = 180) {
  const value = text
    .replace(/```(?:[\w-]+)?/g, "")
    .replace(/[*_`#]+/g, "")
    .replace(/\s+/g, " ")
    .trim()
  if (!value) return "Empty message"
  if (value.length <= max) return value
  return `…${value.slice(-max).trimStart()}`
}

export function listStarterMessages(workspaceID?: string) {
  return items
    .filter((item) => !workspaceID || item.workspaceID === workspaceID)
    .sort((a, b) => b.timeCreated - a.timeCreated || b.messageID.localeCompare(a.messageID))
}

export function isStarterMessage(sessionID: string, messageID: string) {
  if (!sessionID || !messageID) return false
  return listStarterMessages().some((item) => item.sessionID === sessionID && item.messageID === messageID)
}

export function toggleStarterMessage(input: Omit<StarterMessage, "preview" | "timeCreated"> & { preview: string; timeCreated?: number }) {
  const target = server
  if (!target || !input.sessionID || !input.messageID || !input.workspaceID) return false
  const current = listStarterMessages()
  const existing = current.find((item) => item.sessionID === input.sessionID && item.messageID === input.messageID)
  if (existing) {
    items = current.filter((item) => item !== existing)
    writeServerCache(target, items)
    notify()
    void deleteStarter(input.sessionID, input.messageID, target).catch(() => {
      if (server !== target) return
      if (!items.some((item) => item.messageID === existing.messageID)) items = [existing, ...items]
      writeServerCache(target, items)
      notify()
    })
    return false
  }

  const item: StarterMessage = {
    sessionID: input.sessionID,
    messageID: input.messageID,
    workspaceID: input.workspaceID,
    sessionTitle: input.sessionTitle.trim() || "Untitled chat",
    role: input.role,
    preview: starterMessagePreview(input.preview),
    timeCreated: input.timeCreated ?? Date.now(),
  }
  items = boundItems([item, ...current])
  writeServerCache(target, items)
  notify()
  void persistStarter(item, target).catch(() => {
    if (server !== target) return
    items = items.filter((entry) => entry.messageID !== item.messageID)
    writeServerCache(target, items)
    notify()
  })
  return true
}

export function renameStarterMessage(sessionID: string, messageID: string, title: string) {
  const nextTitle = title.trim()
  const target = server
  if (!target || !sessionID || !messageID || !nextTitle) return
  const current = listStarterMessages()
  const existing = current.find((item) => item.sessionID === sessionID && item.messageID === messageID)
  if (!existing || existing.sessionTitle === nextTitle) return
  const renamed = { ...existing, sessionTitle: nextTitle }
  items = current.map((item) => (item === existing ? renamed : item))
  writeServerCache(target, items)
  notify()
  void persistStarter(renamed, target).catch(() => {
    if (server !== target) return
    // Undo only the title so a concurrent model change survives.
    items = items.map((item) =>
      item.sessionID === existing.sessionID && item.messageID === existing.messageID && item.sessionTitle === nextTitle
        ? { ...item, sessionTitle: existing.sessionTitle }
        : item,
    )
    writeServerCache(target, items)
    notify()
  })
}

export function removeStarterMessage(sessionID: string, messageID: string) {
  const target = server
  if (!target || !sessionID || !messageID) return
  const current = listStarterMessages()
  const next = current.filter((item) => item.sessionID !== sessionID || item.messageID !== messageID)
  if (next.length === current.length) return
  items = next
  writeServerCache(target, next)
  notify()
  void deleteStarter(sessionID, messageID, target).catch(() => undefined)
}

export function removeStarterMessagesForSessions(sessionIDs: Iterable<string>) {
  const target = server
  const removed = new Set(sessionIDs)
  if (!target || removed.size === 0) return
  const current = listStarterMessages()
  const deleted = current.filter((item) => removed.has(item.sessionID))
  if (deleted.length === 0) return
  items = current.filter((item) => !removed.has(item.sessionID))
  writeServerCache(target, items)
  notify()
  for (const item of deleted) void deleteStarter(item.sessionID, item.messageID, target).catch(() => undefined)
}

export function useStarterMessagesVersion() {
  const subscription = createStarterMessagesVersion()
  onCleanup(subscription.dispose)
  return subscription.version
}

export function createStarterMessagesVersion() {
  const [value, setValue] = createSignal(version)
  const listener = () => setValue(version)
  listeners.add(listener)
  return {
    version: value,
    dispose: () => listeners.delete(listener),
  }
}
